"use client";

import { cn } from "@/lib/utils";
import logo from "@/assets/images/logo_footer.svg";
import { useTranslation } from "react-i18next";
import { LanguageLink } from "@/components/LanguageLink";
import { useAuth } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSectionNavigation } from "./Header";

interface FooterProps extends React.HTMLAttributes<HTMLElement> {}

function Footer({ className, ...props }: FooterProps) {
  const { goToSection } = useSectionNavigation();
  const { t } = useTranslation();
  const { isLoaded, isSignedIn } = useAuth();

  const footerLinks = [
    { label: t("footer.nav.terms"), href: "/terms" },
    { label: t("footer.nav.privacy"), href: "/privacy" },
    { label: t("footer.nav.howItWorks"), href: "#how-it-works" },
    { label: t("footer.nav.about"), href: "/about" },
    { label: t("footer.nav.pricing"), href: "#pricing" },
    { label: t("footer.nav.faq"), href: "#faq" },
    { label: t("footer.nav.login"), href: "/dashboard" },
  ];

  const legalLinks = [
    { label: t("footer.nav.terms"), href: "/terms" },
    { label: t("footer.nav.privacy"), href: "/privacy" },
    { label: t("footer.nav.about"), href: "/about" },
  ];

  return (
    <div className="w-full bg-lavender-dark">
      <footer
        className={cn(
          "flex flex-col md:flex-row max-w-7xl mx-auto justify-between",
          "px-14 pt-12 pb-12 md:px-6 md:pt-8 md:pb-32",
          "2k:max-w-[80%] 2k:pb-24",
          "4k:max-w-[80%] 4k:pt-24",
          className
        )}
        {...props}
      >
        {/* Logo */}
        <LanguageLink to="/" className="flex items-center mb-6 md:mb-0">
          <img src={logo} className="w-[180px] md:w-[260px] 2k:w-[380px] 4k:w-[580px]"/>
        </LanguageLink>

        {/* Navigation Links */}
        <nav className="flex flex-col md:grid md:grid-cols-2 grid-rows-4 md:grid-flow-col-dense justify-center space-x-12 md:translate-y-6">
          {footerLinks.map((link) => {
            // Use LanguageLink for page navigation, anchor tags for section scrolling
            if (link.href?.startsWith("#")) {
              return (
                <a
                  key={link.href}
                  onClick={() => goToSection(link.href)}
                  className="cursor-pointer font-bold text-[18px] md:text-[24px] 2k:text-[32px] 4k:text-[52px]"
                >
                  {link.label}
                </a>
              );
            } else if (link.href === "/dashboard") {
              return (
                <>
                  {/* Mobile: styled button */}
                  <Button
                    key={`${link.href}-mobile`}
                    variant="default"
                    className="md:hidden w-fit rounded-sm bg-black hover:bg-black/90 font-link text-white px-4 py-5.5 mt-2"
                    asChild
                  >
                    <LanguageLink to={link.href}>
                      {isLoaded ? (isSignedIn ? t("common.dashboard") : t("common.login")) : <Loader2 className="animate-spin" />}
                    </LanguageLink>
                  </Button>
                  {/* Desktop: plain text link */}
                  <LanguageLink
                    key={link.href}
                    to={link.href}
                    className="hidden md:block cursor-pointer font-bold text-[24px] 2k:text-[32px] 4k:text-[52px]"
                  >
                    {isSignedIn ? t("common.dashboard") : t("common.login")}
                  </LanguageLink>
                </>
              );
            } else {
              // For /terms and /privacy, just show the label as a link
              return (
                <LanguageLink
                  key={link.href}
                  to={link.href}
                  className="cursor-pointer font-bold text-[18px] md:text-[24px] 2k:text-[32px] 4k:text-[52px]"
                >
                  {link.label}
                </LanguageLink>
              );
            }
          })}
        </nav>

        {/* Legal Links */}
        <div className="flex flex-col justify-center gap-1 mt-6 md:mt-0 md:translate-y-6">
          <nav className="flex gap-3 mb-1">
            {legalLinks.map((link) => (
              <LanguageLink
                key={link.href}
                to={link.href}
                className="text-[10px] md:text-[12px] 2k:text-[16px] 4k:text-[24px] font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </LanguageLink>
            ))}
          </nav>
          <p className="text-[10px] md:text-[12px] font-bold 2k:text-[16px] 4k:text-[24px]">
            {t("common.copyright", { year: new Date().getFullYear() })}
          </p>
        </div>
      </footer>
    </div>
  );
}

export { Footer };
